/**
 * OrderLineModelViewService
 * Service for OrderLine ModelView endpoints
 * Route: api/orderlines
 * Controller: OrderLineModelViewController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * OrderLineModelViewService
 * Handles order line model view queries (enriched data)
 * Maps to /api/orderlines/{view-endpoints}
 */
export class OrderLineModelViewService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? OrderLineModelViewService.DEFAULT_SERVICE_PATH,
            basePath: OrderLineModelViewService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== SINGLE VIEW ====================
    /**
     * Get order line model view by ID
     * GET /api/orderlines/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Order line view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get order line model view by ID (raw)
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== ALL VIEWS ====================
    /**
     * Get all order line model views
     * GET /api/orderlines/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all order line model views (raw)
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('view-all', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VIEWS BY ORDER ====================
    /**
     * Get order line views by order ID
     * GET /api/orderlines/order/{orderId}/view
     */
    async getViewsByOrder(orderId) {
        const response = await this.getViewsByOrderRaw(orderId);
        return response.data ?? [];
    }
    /**
     * Get order line views by order ID (raw)
     */
    getViewsByOrderRaw(orderId) {
        const endpoint = this.getEndpoint('views-by-order', `order/${orderId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VIEWS BY PRODUCT ====================
    /**
     * Get order line views by product ID
     * GET /api/orderlines/product/{productId}/view
     */
    async getViewsByProduct(productId) {
        const response = await this.getViewsByProductRaw(productId);
        return response.data ?? [];
    }
    /**
     * Get order line views by product ID (raw)
     */
    getViewsByProductRaw(productId) {
        const endpoint = this.getEndpoint('views-by-product', `product/${productId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VIEWS BY LOCATION ====================
    /**
     * Get order line views by location
     * GET /api/orderlines/location/{locationId}/view
     */
    async getViewsByLocation(locationId) {
        const response = await this.getViewsByLocationRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get order line views by location (raw)
     */
    getViewsByLocationRaw(locationId) {
        const endpoint = this.getEndpoint('views-by-location', `location/${locationId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
OrderLineModelViewService.DEFAULT_SERVICE_PATH = 'orders';
OrderLineModelViewService.BASE_PATH = 'api/orderlines';
//# sourceMappingURL=OrderLineModelViewService.js.map